import React, { useEffect, useState, useMemo } from "react";
import Marquee from "react-fast-marquee";

/**
 * M Set / H Set / Hunter Railcar interior dot-matrix indicator.
 * Cycles through a list of "pages" (top + bottom row pairs) at a
 * configurable interval. Falls back to legacy single-page if no
 * pages provided.  Amber dots on pure black with a real
 * dot-matrix mask overlay so unlit dots are visible.
 */
export default function RailcarLEDDisplay({
  railcarTop,
  railcarBottom,
  railcarScroll,
  railcarPages,
  railcarInterval,
}) {
  // Build effective pages list. Empty incoming -> legacy single page.
  const pages = useMemo(() => {
    const list = Array.isArray(railcarPages) ? railcarPages : [];
    const cleaned = list
      .map((p) => ({
        top: (p?.top ?? "").toString(),
        bottom: (p?.bottom ?? "").toString(),
      }))
      .filter((p) => p.top || p.bottom);
    if (cleaned.length > 0) return cleaned;
    return [{
      top: (railcarTop ?? "Welcome aboard").toString(),
      bottom: (railcarBottom ?? "").toString(),
    }];
  }, [railcarPages, railcarTop, railcarBottom]);

  const [idx, setIdx] = useState(0);

  // Reset index when pages array shrinks
  useEffect(() => {
    if (idx >= pages.length) setIdx(0);
  }, [pages.length, idx]);

  // Cycle pages
  useEffect(() => {
    if (pages.length <= 1) return;
    const ms = Math.max(1500, Number(railcarInterval) || 4000);
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % pages.length);
    }, ms);
    return () => clearInterval(t);
  }, [pages.length, railcarInterval]);

  const current = pages[idx] || pages[0];
  const top = current.top;
  const bottom = current.bottom;
  const showPager = pages.length > 1;

  return (
    <div
      data-testid="railcar-led-display"
      className="relative w-full h-full bg-black overflow-hidden select-none font-vt323"
    >
      {/* Subtle CRT vignette */}
      <div className="absolute inset-0 crt-vignette z-10 pointer-events-none" />

      {/* Outer chassis */}
      <div className="absolute inset-0 border-[6px] border-[#0d0d0d] pointer-events-none" />

      {/* Pager dots */}
      {showPager && (
        <div
          className="absolute top-2 right-3 z-20 flex items-center gap-1.5"
          data-testid="railcar-pager"
        >
          {pages.map((_, i) => (
            <span
              key={i}
              className={`inline-block rounded-full transition-opacity ${
                i === idx ? "opacity-100" : "opacity-30"
              }`}
              style={{
                width: 6,
                height: 6,
                backgroundColor: "#FFB000",
                boxShadow: i === idx ? "0 0 6px rgba(255,176,0,0.9)" : "none",
              }}
            />
          ))}
        </div>
      )}

      <div className="relative z-0 w-full h-full flex flex-col items-stretch justify-center px-[clamp(16px,2.6vw,40px)] py-[clamp(10px,2vw,28px)] gap-[clamp(6px,1.1vw,18px)]">
        {/* TOP ROW (static, single line, scales) */}
        <div
          key={`top-${idx}`}
          className="dot-matrix-mask led-glow uppercase w-full text-center whitespace-nowrap overflow-hidden"
          style={{
            fontSize: bottom ? "clamp(22px,7vw,110px)" : "clamp(30px,9vw,150px)",
            lineHeight: 1,
            letterSpacing: "0.06em",
            "--dot-size": "clamp(3px, 0.55vw, 8px)",
          }}
          data-testid="railcar-top-row"
        >
          {top || " "}
        </div>

        {/* BOTTOM ROW (optional, static or scrolling) */}
        {bottom ? (
          <div
            className="w-full overflow-hidden"
            data-testid="railcar-bottom-row-container"
          >
            {railcarScroll ? (
              <Marquee gradient={false} speed={55} pauseOnHover key={`m-${idx}`}>
                <span
                  className="dot-matrix-mask led-glow uppercase whitespace-nowrap inline-block pr-32"
                  style={{
                    fontSize: "clamp(18px,5vw,80px)",
                    lineHeight: 1,
                    letterSpacing: "0.06em",
                    "--dot-size": "clamp(3px, 0.45vw, 7px)",
                  }}
                  data-testid="railcar-bottom-row"
                >
                  {bottom}
                </span>
              </Marquee>
            ) : (
              <div
                key={`bot-${idx}`}
                className="dot-matrix-mask led-glow uppercase w-full text-center whitespace-nowrap overflow-hidden"
                style={{
                  fontSize: "clamp(18px,5vw,80px)",
                  lineHeight: 1,
                  letterSpacing: "0.06em",
                  "--dot-size": "clamp(3px, 0.45vw, 7px)",
                }}
                data-testid="railcar-bottom-row"
              >
                {bottom}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
